import React, { useState } from 'react';
import { AlertTriangle, Bug, CheckCircle } from 'lucide-react';
import * as Sentry from '@sentry/react';

export const SentryTestButton: React.FC = () => {
  const [lastError, setLastError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const testManualException = () => {
    try {
      setLastError(null);
      setTestStatus('idle');

      Sentry.captureException(new Error('Manual Sentry Test Exception from UI'));

      setTestStatus('success');
      setLastError('Manual exception captured successfully!');

      setTimeout(() => {
        setTestStatus('idle');
        setLastError(null);
      }, 5000);
    } catch (err) {
      setTestStatus('error');
      setLastError(`Failed to capture: ${err}`);
    }
  };

  const testThrowError = () => {
    setLastError(null);
    setTestStatus('idle');
    throw new Error('Thrown Sentry Test Error from UI');
  };

  const testAsyncError = async () => {
    setLastError(null);
    setTestStatus('idle');

    await new Promise(resolve => setTimeout(resolve, 100));
    throw new Error('Async Sentry Test Error from UI');
  };

  return (
    <div className="fixed bottom-4 right-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 border-2 border-purple-500 max-w-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded-lg">
            <Bug className="w-6 h-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div>
            <h3 className="font-bold text-gray-900 dark:text-white">Sentry Test</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400">Production Testing</p>
          </div>
        </div>

        <div className="space-y-2 mb-4">
          <button
            onClick={testManualException}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
          >
            Test Manual Capture
          </button>

          <button
            onClick={testThrowError}
            className="w-full bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
          >
            Test Thrown Error
          </button>

          <button
            onClick={testAsyncError}
            className="w-full bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 rounded-lg font-medium transition-colors text-sm"
          >
            Test Async Error
          </button>
        </div>

        {lastError && (
          <div className={`flex items-start gap-2 p-3 rounded-lg ${
            testStatus === 'success'
              ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
              : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-300'
          }`}>
            {testStatus === 'success' ? (
              <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            )}
            <p className="text-xs">{lastError}</p>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Check your Sentry dashboard to see if errors appear.
          </p>
        </div>
      </div>
    </div>
  );
};
